import { useEffect, useState } from "react";
import { Icon } from "./Icon";
import { bizRecordsAPI } from "../../services/bizApi";

export const INVOICE_STATUSES = ["draft", "sent", "paid", "void"];

function StatusPill({ tone = "neutral", children }) {
  return <span className={`category-status ${tone}`}>{children}</span>;
}

const STATUS_TONE = { draft: "neutral", sent: "warn", paid: "good", void: "bad" };

function apiErrorMessage(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d?.msg || String(d)).join("; ") || fallback;
  return fallback;
}

export function LiveInvoiceRow({ inv, onEdit, onDelete }) {
  const initials = (inv.customer_name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();
  return <div className="records-row" role="button" tabIndex={0} onClick={() => onEdit(inv)} onKeyDown={e => { if (e.key === "Enter") onEdit(inv); }}>
    <span className="record-identity"><i>{initials}</i><span><strong>{inv.customer_name}</strong><small>{inv.line_items?.length || 0} line item{(inv.line_items?.length || 0) === 1 ? "" : "s"}{inv.due_date ? ` · due ${inv.due_date}` : ""}</small></span></span>
    <span className="record-category">{inv.issue_date}</span>
    <strong className="record-value">${Number(inv.total).toFixed(2)}</strong>
    <StatusPill tone={STATUS_TONE[inv.status] || "neutral"}>{(inv.status || "draft").charAt(0).toUpperCase() + (inv.status || "draft").slice(1)}</StatusPill>
    <span className="record-row-actions">
      <button type="button" aria-label={`Edit invoice for ${inv.customer_name}`} onClick={(e) => { e.stopPropagation(); onEdit(inv); }}><Icon name="settings" size={15}/></button>
      <button type="button" aria-label={`Delete invoice for ${inv.customer_name}`} onClick={(e) => { e.stopPropagation(); onDelete(inv); }}>×</button>
    </span>
  </div>;
}

function computeTotals(lineItems, taxRate, discountAmount) {
  const subtotal = lineItems.reduce((s, li) => s + (parseFloat(li.quantity) || 0) * (parseFloat(li.rate) || 0), 0);
  const discount = parseFloat(discountAmount) || 0;
  const taxable = Math.max(0, subtotal - discount);
  const taxAmount = taxable * ((parseFloat(taxRate) || 0) / 100);
  return { subtotal, taxAmount, total: taxable + taxAmount };
}

export function InvoiceFormDialog({ editing, onClose, onSaved, onDelete, live }) {
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState(editing?.contact_id || "");
  const [customerName, setCustomerName] = useState(editing?.customer_name || "");
  const [customerEmail, setCustomerEmail] = useState(editing?.customer_email || "");
  const [issueDate, setIssueDate] = useState(editing?.issue_date || new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(editing?.due_date || "");
  const [notes, setNotes] = useState(editing?.notes || "");
  const [lineItems, setLineItems] = useState(
    editing?.line_items?.length ? editing.line_items.map(li => ({ description: li.description, quantity: String(li.quantity), rate: String(li.rate) }))
      : [{ description: "", quantity: "1", rate: "" }]
  );
  const [taxRate, setTaxRate] = useState(editing ? String(editing.tax_rate) : "0");
  const [discountAmount, setDiscountAmount] = useState(editing ? String(editing.discount_amount) : "0");
  const [collaborators, setCollaborators] = useState(
    editing?.collaborators?.map(c => ({ name: c.name, email: c.email || "", amount: String(c.amount) })) || []
  );
  const [status, setStatus] = useState(editing?.status || "draft");
  const [fieldError, setFieldError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    bizRecordsAPI.listContacts({ page_size: 100 }).then(({ data }) => setContacts(data.contacts || [])).catch(() => {});
  }, []);

  const totals = computeTotals(lineItems, taxRate, discountAmount);

  const onSelectContact = (id) => {
    setContactId(id);
    const c = contacts.find(x => x.contact_id === id);
    if (c) { setCustomerName(c.name); setCustomerEmail(c.email || ""); }
  };

  const updateLineItem = (index, field, value) => setLineItems(items => items.map((li, i) => i === index ? { ...li, [field]: value } : li));
  const addLineItem = () => setLineItems(items => [...items, { description: "", quantity: "1", rate: "" }]);
  const removeLineItem = (index) => setLineItems(items => items.length > 1 ? items.filter((_, i) => i !== index) : items);

  const updateCollaborator = (index, field, value) => setCollaborators(list => list.map((c, i) => i === index ? { ...c, [field]: value } : c));
  const addCollaborator = () => setCollaborators(list => [...list, { name: "", email: "", amount: "" }]);
  const removeCollaborator = (index) => setCollaborators(list => list.filter((_, i) => i !== index));

  const validate = () => {
    if (!customerName.trim()) return "Customer name is required.";
    if (!issueDate) return "Issue date is required.";
    const items = lineItems.filter(li => li.description.trim());
    if (items.length === 0) return "Add at least one line item.";
    for (const li of items) {
      const qty = parseFloat(li.quantity), rate = parseFloat(li.rate);
      if (isNaN(qty) || qty <= 0) return `Enter a quantity greater than 0 for "${li.description}".`;
      if (isNaN(rate) || rate < 0) return `Enter a valid rate for "${li.description}".`;
    }
    const { subtotal, total } = computeTotals(items, taxRate, discountAmount);
    if ((parseFloat(discountAmount) || 0) > subtotal) return "Discount cannot exceed the invoice subtotal.";
    const activeCollabs = collaborators.filter(c => c.name.trim());
    for (const c of activeCollabs) {
      if (isNaN(parseFloat(c.amount)) || parseFloat(c.amount) <= 0) return `Enter a valid allocation amount for "${c.name}".`;
    }
    const allocated = activeCollabs.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
    if (allocated > total) return "Collaborator allocations cannot exceed the invoice total.";
    if (editing && (status === "sent" || status === "paid") && total <= 0) return "Invoice must have a total greater than $0 before it can be sent or marked paid.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { setFieldError(err); return; }
    setFieldError(null);
    setSubmitError(null);
    setSaving(true);
    try {
      const items = lineItems.filter(li => li.description.trim()).map(li => ({
        description: li.description.trim(), quantity: parseFloat(li.quantity), rate: parseFloat(li.rate),
      }));
      const activeCollabs = collaborators.filter(c => c.name.trim()).map(c => ({
        name: c.name.trim(), email: c.email.trim() || null, amount: parseFloat(c.amount),
      }));
      const fields = {
        contact_id: contactId || null, customer_name: customerName.trim(), customer_email: customerEmail.trim() || null,
        issue_date: issueDate, due_date: dueDate || null, notes: notes.trim() || null,
        line_items: items, tax_rate: parseFloat(taxRate) || 0, discount_amount: parseFloat(discountAmount) || 0,
        collaborators: activeCollabs,
      };
      if (editing) await live.update(editing.invoice_id, { ...fields, status });
      else await live.create(fields);
      await live.reload();
      onSaved(editing ? `Invoice for ${customerName} updated.` : `Invoice for ${customerName} created.`);
    } catch (e) {
      if (e?.response?.status === 403) setSubmitError("Your role doesn't have permission to save invoices.");
      else setSubmitError(apiErrorMessage(e, "Couldn't save this invoice — please try again."));
    } finally {
      setSaving(false);
    }
  };

  return <div className="overlay-backdrop" onMouseDown={onClose}><section className="dialog workspace-dialog" role="dialog" aria-modal="true" aria-labelledby="invoice-dialog-title" onMouseDown={event => event.stopPropagation()}>
    <button className="dialog-close" onClick={onClose} aria-label="Close" data-testid="invoice-dialog-close">×</button>
    <span className="dialog-icon"><Icon name="invoice" size={21}/></span>
    <p className="panel-kicker">Invoices</p><h2 id="invoice-dialog-title">{editing ? "Edit invoice" : "Create invoice"}</h2>
    <div className="mini-form">
      <label>Contact<select data-testid="invoice-form-contact" value={contactId} onChange={e => onSelectContact(e.target.value)}>
        <option value="">Choose a saved contact (optional)</option>
        {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name}</option>)}
      </select></label>
      <label>Customer name<input data-testid="invoice-form-customer-name" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Bayview Media"/></label>
      <label>Customer email<input data-testid="invoice-form-customer-email" type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="ap@company.com"/></label>
      <label>Issue date<input data-testid="invoice-form-issue-date" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)}/></label>
      <label>Due date<input data-testid="invoice-form-due-date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}/></label>
      {editing && <label>Status<select data-testid="invoice-form-status" value={status} onChange={e => setStatus(e.target.value)}>{INVOICE_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}</select></label>}
    </div>

    <div className="mini-form-section" data-testid="invoice-line-items">
      <p className="panel-kicker">Line items</p>
      {lineItems.map((li, i) => <div className="line-item-row" key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
        <input data-testid={`invoice-line-item-description-${i}`} style={{ flex: 2 }} value={li.description} onChange={e => updateLineItem(i, "description", e.target.value)} placeholder="Description"/>
        <input data-testid={`invoice-line-item-quantity-${i}`} style={{ flex: 1 }} type="number" step="0.01" value={li.quantity} onChange={e => updateLineItem(i, "quantity", e.target.value)} placeholder="Qty"/>
        <input data-testid={`invoice-line-item-rate-${i}`} style={{ flex: 1 }} type="number" step="0.01" value={li.rate} onChange={e => updateLineItem(i, "rate", e.target.value)} placeholder="Rate"/>
        <button type="button" data-testid={`invoice-line-item-remove-${i}`} onClick={() => removeLineItem(i)} aria-label="Remove line item">×</button>
      </div>)}
      <button type="button" className="secondary-action" data-testid="invoice-add-line-item" onClick={addLineItem}>+ Add line item</button>
    </div>

    <div className="mini-form">
      <label>Tax rate (%)<input data-testid="invoice-form-tax-rate" type="number" step="0.01" value={taxRate} onChange={e => setTaxRate(e.target.value)}/></label>
      <label>Discount ($)<input data-testid="invoice-form-discount" type="number" step="0.01" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)}/></label>
      <label>Notes<input data-testid="invoice-form-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add context for your team"/></label>
    </div>

    <div className="mini-form-section" data-testid="invoice-collaborators">
      <p className="panel-kicker">Collaborator allocations</p>
      {collaborators.map((c, i) => <div className="line-item-row" key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
        <input data-testid={`invoice-collaborator-name-${i}`} style={{ flex: 2 }} value={c.name} onChange={e => updateCollaborator(i, "name", e.target.value)} placeholder="Collaborator name"/>
        <input data-testid={`invoice-collaborator-email-${i}`} style={{ flex: 2 }} value={c.email} onChange={e => updateCollaborator(i, "email", e.target.value)} placeholder="Email (optional)"/>
        <input data-testid={`invoice-collaborator-amount-${i}`} style={{ flex: 1 }} type="number" step="0.01" value={c.amount} onChange={e => updateCollaborator(i, "amount", e.target.value)} placeholder="Amount"/>
        <button type="button" data-testid={`invoice-collaborator-remove-${i}`} onClick={() => removeCollaborator(i)} aria-label="Remove collaborator">×</button>
      </div>)}
      <button type="button" className="secondary-action" data-testid="invoice-add-collaborator" onClick={addCollaborator}>+ Add collaborator</button>
    </div>

    <div className="invoice-totals-preview" data-testid="invoice-totals-preview" style={{ margin: "12px 0", fontSize: 14 }}>
      <div>Subtotal: <strong>${totals.subtotal.toFixed(2)}</strong></div>
      <div>Tax: <strong>${totals.taxAmount.toFixed(2)}</strong></div>
      <div>Total: <strong>${totals.total.toFixed(2)}</strong></div>
    </div>

    {fieldError && <p className="dialog-error" role="alert" data-testid="invoice-form-validation-error">{fieldError}</p>}
    {submitError && <p className="dialog-error" role="alert" data-testid="invoice-form-submit-error">{submitError}</p>}
    <button className="dialog-primary" onClick={submit} disabled={saving} data-testid="invoice-form-submit">{saving ? "Saving…" : editing ? "Save changes" : "Create invoice"}</button>
    {editing && <button className="secondary-action" style={{ marginTop: 8 }} onClick={() => onDelete(editing)} data-testid="invoice-form-delete">Delete invoice</button>}
  </section></div>;
}
